import '../../i18n/autoTranslate';
// AICoachingCard.jsx
// AI Coach card: highlights insight + recommended action + small metric chips.
//
// FIX: The original component used props `headline / insight / focusAreas /
// onPrimary` but the dashboard (index.jsx) passes `title / description / tags
// / onCta`. Result: the card always rendered the default placeholder text
// ("Your problem-solving dipped...") regardless of real backend data, and
// the CTA button did nothing.
//
// We now accept BOTH naming styles so existing callers (and any future ones)
// keep working. The dashboard's actual props are listed first.

import React, { useMemo } from 'react';
import { Button } from '../../../components/ui/shadcn/button';
import { Badge } from '../../../components/ui/shadcn/badge';
import { Sparkles, TrendingDown, TrendingUp, ArrowRight } from 'lucide-react';

function TrendPill({ trend }) {
  const isDown = trend === 'down';
  const Icon = isDown ? TrendingDown : TrendingUp;

  return (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs',
        isDown
          ? 'border-rose-400/20 bg-rose-500/10 text-rose-200'
          : 'border-emerald-400/20 bg-emerald-500/10 text-emerald-200',
      ].join(' ')}
    >
      <Icon className="h-3.5 w-3.5" />
      {isDown ? 'Needs focus' : 'Improving'}
    </span>
  );
}

export default function AICoachingCard({
  // === Dashboard-style props (preferred) ===
  title,
  description,
  tags,
  onCta,
  ctaLabel,

  // === Legacy / detailed props (still supported) ===
  headline,
  insight,
  focusAreas,
  onPrimary,
  onSecondary,
  primaryCtaLabel = 'Start Stress Interview',
  secondaryCtaLabel,

  // Visual flags
  trend = 'down',
  snapshot, // optional { label, score, delta }
}) {
  // Resolve which prop set the caller actually used.
  const resolvedTitle = title || 'AI Coach';
  const resolvedHeadline =
    headline ||
    (title && title !== 'AI Coach' && title !== 'AI Coaching' ? title : 'Recommendation for you');
  const resolvedInsight =
    description || insight || `Try a "Stress Interview" to rebuild speed and structure.`;
  const resolvedTags = (tags && tags.length ? tags : focusAreas) || [];
  const resolvedPrimaryHandler = onCta || onPrimary;
  const resolvedPrimaryLabel = ctaLabel || primaryCtaLabel;

  const deltaText = useMemo(() => {
    if (!snapshot || snapshot.delta == null) return null;
    const sign = snapshot.delta > 0 ? '+' : '';
    return `${sign}${snapshot.delta}%`;
  }, [snapshot]);

  return (
    <section className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_14px_50px_rgba(0,0,0,0.35)]">
      {/* Gradient wash */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-purple-500/14 via-transparent to-blue-500/14" />
      {/* Soft glow blob */}
      <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-purple-500/15 blur-3xl" />

      <div className="relative z-10 p-5 sm:p-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-purple-400/20 bg-purple-500/10 text-purple-200 shrink-0">
              <Sparkles className="h-5 w-5" />
            </div>

            <div className="min-w-0">
              <p className="text-xs font-medium text-purple-200/90">{resolvedTitle}</p>
              <h3 className="mt-1 text-base font-semibold text-white sm:text-lg">
                {resolvedHeadline}
              </h3>
              <p className="mt-1 text-sm text-gray-300">{resolvedInsight}</p>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <TrendPill trend={trend} />
                {resolvedTags.slice(0, 4).map((t) => (
                  <Badge
                    key={t}
                    variant="secondary"
                    className="rounded-full border border-white/10 bg-white/5 text-gray-200"
                  >
                    {t}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Snapshot (only when caller supplies real data) */}
          {snapshot && (
            <div className="w-full sm:w-auto">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-gray-400">{snapshot.label}</p>
                  {deltaText && (
                    <span
                      className={[
                        'text-xs font-semibold',
                        snapshot.delta < 0 ? 'text-rose-300' : 'text-emerald-300',
                      ].join(' ')}
                    >
                      {deltaText}
                    </span>
                  )}
                </div>

                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-2xl font-semibold text-white">
                    {Math.round(snapshot.score)}
                  </span>
                  <span className="text-xs text-gray-400">/ 100</span>
                </div>

                <div className="mt-3 h-2 w-full rounded-full bg-white/10">
                  <div
                    className="h-2 rounded-full bg-purple-400/70"
                    style={{
                      width: `${Math.max(0, Math.min(100, snapshot.score))}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-gray-400">
            Tip: do 1 stress session + 1 normal session to compare improvement.
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            {onSecondary && secondaryCtaLabel && (
              <Button
                type="button"
                onClick={onSecondary}
                variant="outline"
                className="h-10 rounded-xl border-white/10 bg-transparent text-gray-200 hover:bg-white/5"
              >
                {secondaryCtaLabel}
              </Button>
            )}

            <Button
              type="button"
              onClick={resolvedPrimaryHandler}
              disabled={!resolvedPrimaryHandler}
              className="h-10 rounded-xl bg-purple-500/90 text-white hover:bg-purple-500 disabled:opacity-50"
            >
              {resolvedPrimaryLabel}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
